import { addSeconds, endOfDay, format, setMilliseconds } from "date-fns";
import { identity, merge, pickBy, uniq } from "lodash";
import { Descendant } from "slate";

import { DateFormat, timezone } from "constants/date";
import {
  AgentAssignmentFilterEnumMap,
  AttachmentsFilterEnumMap,
  CustomDateRangeFilterId,
  Filter,
  IDateRangeFilter,
  IFilters,
  NotTaggedFilterId,
  RatingFilter,
} from "constants/filters";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { TicketsBatchAction, batchActionStatusMap } from "constants/tickets/batch-action";
import { flattenObjectForUrl } from "helpers/url";
import {
  IReplyPaths,
  ISimilarTicket,
  Rating,
  TicketPriority,
  TicketSilo,
  TicketStatus,
} from "interfaces/entities/ticket";
import { IMacroAction } from "store/entities/macros/interfaces";
import {
  IFetchClosestTicketPayload,
  IFetchSimilarTicketsPayload,
  IFetchTicketsCollectionPayload,
  ITicketRequester,
} from "store/entities/tickets/interfaces";
import { IPerformBulkActionPayload, ITicketsViewBatchUpdatePayloadData } from "store/views/tickets/interfaces";

import { IKeyAny, IRequestResult } from "../api-client";
import { BaseApi } from "../base";

import { IGenerateReplySuggestionResponse, ReplySuggestionRating } from "./interfaces";

export interface ITicketCreateParams {
  requester: ITicketRequester;
  chatId?: string;
  subject?: string;
  message?: string | Descendant[];
  priority?: TicketPriority;
  assigneeId?: string;
  assignedTeamId: string;
  richTextPresent?: boolean;
  attachments?: File[];
  transactionID?: string;
  isPrivate?: boolean;
  tags?: string[];
  status?: TicketStatus;
  cannedResponseIds?: string[];
  peopleInTheLoop?: string[];
}

export interface IFetchTicketsParams extends IFilters {
  status: string; // eslint-disable-line no-restricted-globals
}

export interface IUpdateTicketTagsParams {
  tagIds: string[];
}

export interface IAddTicketTagsParams {
  tagIds: string[];
  suggestedTagIds?: string[];
}

export interface IUpdateTicketSubjectParams {
  subject: string;
}

export interface IUpdateTicketRequesterParams {
  requesterName: string;
  requesterEmail: string;
}

export interface IUpdateTicketCCsParams {
  ccs: string[];
}

export interface IUpdateTicketCustomFieldParams {
  customFields: Record<string, string>;
}

export interface IUpdateTicketCustomFieldResponse {
  customFields: Record<string, string>;
}

export interface IUpdateTicketSiloParams {
  silo: TicketSilo;
}

export interface IUpdateTicketCCsResponse {
  ccs: {
    mail: string;
  }[];
}

export interface IUpdateTicketAssigneeParams {
  assigneeId: string;
}

export interface IUpdateTicketAssignmentParams {
  teamId: string;
  visibilityTeamIds: string[];
}

export interface ISendTicketMessageParams {
  message: string | Descendant[];
  richTextPresent?: boolean;
  isPrivate: boolean;
  attachments?: File[];
  transactionID?: string;
  status?: TicketStatus;
  assigneeId?: string;
  cannedResponseIds?: string[];
  replySuggestionIds?: string[];
}

export interface IRateTicketParams {
  token: string;
  rating: Rating;
  comment: string;
}

export function getBatchParams(
  ticketIds: string[],
  batchAction: TicketsBatchAction,
  data?: ITicketsViewBatchUpdatePayloadData
): IKeyAny {
  // TODO: type it properly
  const params: IKeyAny = {
    ids: ticketIds,
  };

  const fields: IKeyAny = {};
  if (batchAction === TicketsBatchAction.ChangeAssignee) {
    fields.assignment = {
      agent: {
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        ID: data.assignee.id,
      },
    };
  } else if (batchAction === TicketsBatchAction.ChangeTeam) {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    fields.teamIDs = [data.team.id];
  } else {
    fields.status = batchActionStatusMap[batchAction];
  }

  params.fields = fields;
  return params;
}

function getUpdateTagsParams(params: IUpdateTicketTagsParams): IKeyAny {
  return {
    tagIDs: params.tagIds,
  };
}

export function getUpdateRequesterParams(params: IUpdateTicketRequesterParams): IKeyAny {
  return {
    requester: {
      name: params.requesterName,
      email: params.requesterEmail,
    },
  };
}

export function getUpdateAssigneeParams(params: IUpdateTicketAssigneeParams): IKeyAny {
  if (params.assigneeId) {
    return {
      assignment: {
        agent: {
          ID: params.assigneeId,
        },
      },
    };
  }
  return {
    assignment: {
      agent: null,
    },
  };
}

function getUpdateAssignmentParams(params: IUpdateTicketAssignmentParams): IKeyAny {
  return {
    assignment: {
      agent: null,
      team: params.teamId ? { ID: params.teamId } : null,
    },
    teamIDs: params.visibilityTeamIds,
  };
}

export function getUpdateCCsParams(params: IUpdateTicketCCsParams): IKeyAny {
  return {
    cc: params.ccs.map(cc => ({
      email: cc,
    })),
  };
}

export function getCreateTicketParams(params: ITicketCreateParams): FormData | IKeyAny {
  // Switch to formdata format of sending ticket params if legacy attachments mechanism is used.
  if (params.attachments !== undefined && params.attachments !== null) {
    return getCreateTicketParamsAsFormData(params);
  }

  const {
    requester: { mail, name },
    chatId,
    subject,
    assigneeId,
    assignedTeamId,
    priority,
    transactionID,
    richTextPresent,
    message,
    isPrivate,
    tags,
    status,
    cannedResponseIds,
    peopleInTheLoop,
  } = params;

  return {
    requester: {
      email: mail,
      ...(name ? { name } : {}),
    },
    ...(chatId ? { chat_id: chatId } : {}),
    subject,
    ...(typeof message === "string" || richTextPresent
      ? {
          message: {
            ...(typeof message === "string" ? { text: message } : {}),
            ...(richTextPresent ? { richTextObj: message } : {}),
            ...(cannedResponseIds && cannedResponseIds.length > 0 ? { cannedResponseIDs: cannedResponseIds } : {}),
          },
        }
      : {}),
    assignment: {
      ...(assigneeId ? { agent: { ID: assigneeId } } : { agent: null }),
      team: { ID: assignedTeamId },
    },
    teamIDs: [assignedTeamId],
    status,
    ...(priority ? { priority } : {}),
    ...(transactionID ? { transactionID } : {}),
    isPrivate,
    tagIDs: tags,
    ...(peopleInTheLoop &&
      peopleInTheLoop.length > 0 && {
        cc: peopleInTheLoop.map(cc => ({
          email: cc,
        })),
      }),
  };
}

export function getCreateTicketParamsAsFormData(params: ITicketCreateParams): FormData {
  const data = new FormData();
  data.append("requester[email]", params.requester.mail);
  if (params.requester.name) {
    data.append("requester[name]", params.requester.name);
  }
  if (params.chatId) {
    data.append("chat_id", params.chatId);
  }
  if (params.subject) {
    data.append("subject", params.subject);
  }
  if (params.message) {
    if (typeof params.message === "string") {
      data.append("message[text]", params.message);
    } else if (params.richTextPresent) {
      data.append("message[richTextObj]", JSON.stringify(params.message));
    }
  }
  if (params.assigneeId) {
    data.append("assignment[agent][ID]", params.assigneeId);
  } else {
    data.append("assignment[agent]", "");
  }
  data.append("assignment[team][ID]", params.assignedTeamId);
  data.append("teamIDs[]", params.assignedTeamId);
  if (params.tags && params.tags.length > 0) {
    params.tags.forEach(tag => {
      data.append("tagIDs[]", tag);
    });
  }
  // @ts-expect-error TS(2769): No overload matches this call.
  data.append("status", params.status);
  if (params.priority) {
    data.append("priority", params.priority.toString());
  }
  if (params.transactionID) {
    data.append("transactionID", params.transactionID);
  }

  if (params.attachments) {
    params.attachments.forEach(attachment => {
      data.append("attachments", attachment, attachment.name);
    });
  }

  if (params.peopleInTheLoop) {
    params.peopleInTheLoop.forEach((cc, index) => {
      data.append(`cc[${index}][email]`, cc);
    });
  }

  return data;
}

export function getSendMessageParams(params: ISendTicketMessageParams): FormData | IKeyAny {
  // Switch to formdata format of sending ticket params if legacy attachments mechanism is used.
  if (params.attachments !== undefined && params.attachments !== null) {
    return getSendMessageParamsAsFormData(params);
  }

  const {
    message,
    richTextPresent,
    status,
    assigneeId,
    transactionID,
    isPrivate,
    cannedResponseIds,
    replySuggestionIds,
  } = params;

  return {
    isPrivate,
    status,
    ...(typeof message === "string" || richTextPresent
      ? {
          message: {
            ...(typeof message === "string" ? { text: message } : {}),
            ...(richTextPresent ? { richTextObj: message } : {}),
            ...(cannedResponseIds && cannedResponseIds.length > 0 ? { cannedResponseIDs: cannedResponseIds } : {}),
            ...(replySuggestionIds && replySuggestionIds.length > 0 ? { replySuggestionIDs: replySuggestionIds } : {}),
          },
        }
      : {}),
    ...(assigneeId ? { assignment: { agent: { ID: assigneeId } } } : {}),
    transactionID,
  };
}

export function getSendMessageParamsAsFormData(params: ISendTicketMessageParams): FormData {
  const data = new FormData();
  data.append("isPrivate", String(params.isPrivate));

  if (params.message) {
    if (typeof params.message === "string") {
      data.append("message[text]", params.message);
    } else if (params.richTextPresent) {
      data.append("message[richTextObj]", JSON.stringify(params.message));
    }
  }
  if (params.status) {
    data.append("status", params.status);
  }
  if (params.assigneeId) {
    data.append("assignment[agent][ID]", params.assigneeId);
  }
  if (params.attachments) {
    params.attachments.forEach(attachment => {
      data.append("attachments", attachment, attachment.name);
    });
  }
  if (params.transactionID) {
    data.append("transactionID", params.transactionID);
  }

  return data;
}

export function getRateTicketParams(params: IRateTicketParams): IKeyAny {
  return {
    token: params.token,
    rate: params.rating,
    comment: params.comment,
  };
}

const routeStatusMap: { [key in TicketsRoutes]?: TicketStatus } = {
  [TicketsRoutes.Unassigned]: TicketStatus.Open,
  [TicketsRoutes.MyOpen]: TicketStatus.Open,
  [TicketsRoutes.Open]: TicketStatus.Open,
  [TicketsRoutes.Pending]: TicketStatus.Pending,
  [TicketsRoutes.OnHold]: TicketStatus.OnHold,
  [TicketsRoutes.Solved]: TicketStatus.Solved,
  [TicketsRoutes.Closed]: TicketStatus.Closed,
};

export function getStatusForRoute(route: TicketsRoutes): TicketStatus | null {
  return routeStatusMap[route] || null;
}

const ticketRatingMap = {
  [RatingFilter.Rated]: "any",
  [RatingFilter.NotRated]: "none",
  [RatingFilter.RatedGood]: "good",
  [RatingFilter.RatedNeutral]: "neutral",
  [RatingFilter.RatedBad]: "bad",
};

function mapDateRange(
  values: IDateRangeFilter,
  fromPropertyName: string,
  toPropertyName: string,
  dateFormat: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toTransformer: (value: any) => any = identity
): Record<string, string> {
  return {
    ...(values.from && {
      [fromPropertyName]: typeof values.from === "string" ? values.from : format(values.from, dateFormat),
    }),
    ...(values.to && {
      [toPropertyName]: typeof values.to === "string" ? values.to : format(toTransformer(values.to), dateFormat),
    }),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterMappers: Partial<{ [key in Filter]: (values: IFilters[key]) => any }> = {
  [Filter.Followers]: values => ({ follower: values || null }),
  [Filter.Agent]: values => ({
    assignment: {
      agent: {
        ID: values,
      },
    },
  }),
  [Filter.AgentAssignment]: values => {
    // @ts-expect-error TS(18048)
    const value = AgentAssignmentFilterEnumMap.toQueryString[values[0]];
    return {
      hasAssignee: value !== undefined ? value : null, // value can be false, we don't want to return null then
    };
  },
  [Filter.Attachments]: values => {
    // @ts-expect-error TS(18048)
    const value = AttachmentsFilterEnumMap.toQueryString[values[0]];
    return {
      hasAttachments: value !== undefined ? value : null,
    };
  },
  [Filter.Group]: values => ({
    assignment: {
      team: {
        ID: (values?.includes("MY_TEAMS") ? "MY_TEAMS" : values) || null,
      },
    },
  }),
  [Filter.Rating]: values => ({
    rate: values?.map(value => ticketRatingMap[value]) || null,
  }),
  [Filter.TicketStatus]: values => {
    return values ? { status: values } : null;
  },
  [Filter.ResponseAuthor]: values => ({
    // @ts-expect-error TS(18048)
    response_author: values[0] || null,
  }),
  // @ts-expect-error TS(2345)
  [Filter.DateRange]: values => mapDateRange(values, "date_from", "date_to", "YYYY-MM-DD"),
  // @ts-expect-error TS(2345)
  [Filter.RatingDateRange]: values => mapDateRange(values, "rate_date_from", "rate_date_to", "YYYY-MM-DD"),
  [Filter.ResolutionDateRange]: values =>
    // @ts-expect-error TS(2345)
    mapDateRange(values, "resolution_date_from", "resolution_date_to", "YYYY-MM-DD"),
  // @ts-expect-error TS(2345)
  [Filter.ResponseDateRange]: values => mapDateRange(values, "response_date_from", "response_date_to", "YYYY-MM-DD"),
  [Filter.CreationDateRange]: values =>
    mapDateRange(
      // @ts-expect-error TS(2345)
      values,
      "createdDateFrom",
      "createdDateTo",
      DateFormat.ISO,
      values?.id === CustomDateRangeFilterId.CustomPeriod
        ? (d): Date => setMilliseconds(addSeconds(endOfDay(d), 1), 0)
        : endOfDay
    ),
  [Filter.LastActivityDateRange]: values =>
    mapDateRange(
      // @ts-expect-error TS(2345)
      values,
      "updatedDateFrom",
      "updatedDateTo",
      DateFormat.ISO,
      values?.id === CustomDateRangeFilterId.CustomPeriod
        ? (d): Date => setMilliseconds(addSeconds(endOfDay(d), 1), 0)
        : endOfDay
    ),
  [Filter.LastMessageDateRange]: values =>
    mapDateRange(
      // @ts-expect-error TS(2345)
      values,
      "lastMessageFrom",
      "lastMessageTo",
      DateFormat.ISO,
      values?.id === CustomDateRangeFilterId.CustomPeriod
        ? (d): Date => setMilliseconds(addSeconds(endOfDay(d), 1), 0)
        : endOfDay
    ),
  [Filter.RequesterEmail]: values => ({
    requester: {
      // @ts-expect-error TS(18048)
      email: values[0] || null,
    },
  }),
  [Filter.Tag]: values => {
    const notTaggedIndex = (values || []).indexOf(NotTaggedFilterId);

    if (notTaggedIndex !== -1) {
      return {
        hasTags: false,
        // @ts-expect-error TS(18048)
        tagIDs: [...values.slice(0, notTaggedIndex), ...values.slice(notTaggedIndex + 1)],
      };
    }
    return {
      tagIDs: values || null,
    };
  },
  // @ts-expect-error TS(18048)
  [Filter.Priority]: value => ({ priority: value.priority, priorityOp: value.priorityOp || "eq" }),
};

export function serializeFilters(filters: IFilters): Record<string, string> {
  if (!filters) {
    return {};
  }
  return Object.keys(pickBy(filters, identity)).reduce((acc, filterName) => {
    // FIXME: Fix this the next time the file is edited.
    // @ts-expect-error TS(7053)
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const mapper = filterMappers[filterName] || (value => ({ [filterName]: value }));
    // @ts-expect-error TS(7053)
    return merge(acc, mapper(filters[filterName]));
  }, {});
}

export const parseFetchTicketsParams = (params: IFetchTicketsCollectionPayload): { [key: string]: string } => {
  let finalParams: IKeyAny = {};

  if (params.query) {
    const isQueryHDQL = params.query.includes("customFields.");

    if (isQueryHDQL) {
      finalParams.hdql = params.query;
    } else {
      finalParams.query = params.query;
    }
  }

  if (params.hasParent === false) {
    finalParams.hasParent = false;
  }

  finalParams = {
    ...finalParams,
    // @ts-expect-error TS(2345)
    ...serializeFilters(params.filters),
  };

  finalParams.sortBy = "lastMessageAt";
  finalParams.order = "desc";

  if (params.page && params.page > 1) {
    finalParams.page = String(params.page);
  }

  if (params.shortId) {
    finalParams.shortID = params.shortId;
  }

  if (params.maxCount !== undefined) {
    finalParams.maxCount = params.maxCount;
  }

  if (params.eventsScope) {
    finalParams.eventsScope = params.eventsScope;
  }

  if (params.pageSize) {
    finalParams.pageSize = params.pageSize;
  }

  if (params.silo && params.silo !== TicketSilo.Tickets) {
    finalParams.silo = params.silo;
  }

  if (params.sorting) {
    finalParams.sortBy = params.sorting.field;
    finalParams.order = params.sorting.order;
  }

  finalParams.timezone = timezone;

  return flattenObjectForUrl(finalParams);
};

const getPendingActionsParams = (
  visibleTeamIDs: string[],
  tagIDs: string[],
  params: IPerformBulkActionPayload["pendingActions"]
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
) => {
  const result: IKeyAny = {};

  if (typeof params.priority === "number") {
    result.priority = params.priority;
  }

  if (params.status) {
    result.status = params.status;
  }

  if (params.teamId) {
    const teamIDs = uniq([...visibleTeamIDs, params.teamId]);
    result.assignment = { agent: null, team: { ID: params.teamId } };
    result.teamIDs = teamIDs;
  }

  if (params.agentId !== null) {
    const willBeUnassigned = params.agentId === "";
    const agentToAssign = willBeUnassigned ? null : { ID: params.agentId };
    if (result.assignment) {
      result.assignment.agent = agentToAssign;
    } else {
      result.assignment = { agent: agentToAssign };
    }
  }

  if (params.tagIds.length > 0) {
    const newTagIDs = uniq([...tagIDs, ...params.tagIds]);
    result.tagIDs = newTagIDs;
  }

  return result;
};

export default class TicketApi extends BaseApi {
  protected readonly prefix = "tickets";

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createTicket = (params: ITicketCreateParams) => this.post("", getCreateTicketParams({ ...params }));
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteTicket = (ticketId: string) => this.delete(ticketId);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetch = (ticketId: string) => this.get(ticketId);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchAll = (params: IFetchTicketsCollectionPayload) => {
    const defaultParams = { eventsScope: "preview", pageSize: 40 };

    return this.get("", { ...defaultParams, ...parseFetchTicketsParams(params) });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchClosestTicket = (payload: IFetchClosestTicketPayload) => {
    const { params, ticketInfo } = payload;
    const { direction, ticketId, value } = ticketInfo;

    const closestTicketParams = {
      [`${direction}[ID]`]: ticketId,
      [`${direction}[value]`]: value,
    };

    return this.get("", { pageSize: 1, ...parseFetchTicketsParams(params), ...closestTicketParams });
  };

  fetchSimilarTickets = (payload: IFetchSimilarTicketsPayload): Promise<IRequestResult<ISimilarTicket[]>> => {
    const { ticketId } = payload;
    return this.get(`${ticketId}/similar`);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchTicketsToMerge = (params: IFetchTicketsCollectionPayload) =>
    this.get("", {
      ...parseFetchTicketsParams(params),
      eventsScope: "none",
      pageSize: 11,
      sortBy: "createdAt",
      order: "desc",
      "assignment.team.ID": "MY_TEAMS",
      maxCount: 0,
    });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchOriginalMessage = (ticketId: string, eventId: string) =>
    this.get(`${ticketId}/events/${eventId}/originalContent`);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchUnstrippedMessage = (ticketId: string, eventId: string) =>
    this.get(`${ticketId}/events/${eventId}/unstrippedContent`);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  summarize = (ticketId: string) => this.post(`${ticketId}/summary`, {});

  fetchTagSuggestions = (ticketId: string): Promise<IRequestResult<unknown>> => this.put(`${ticketId}/suggestTags`);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  applyMacro = (ticketId: string, actions: IMacroAction[], macroId: string) =>
    this.patch(`${ticketId}`, { actions: { list: actions, macroIDs: [macroId] } });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  batch = (ticketIds: string[], batchAction: TicketsBatchAction, data?: ITicketsViewBatchUpdatePayloadData) =>
    this.patch("", getBatchParams(ticketIds, batchAction, data));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  mergeTickets = (parentTicketId: string, ticketId: string) =>
    this.post(`${parentTicketId}/childTickets`, { childTicketID: ticketId });

  updateTags = (ticketId: string, params: IUpdateTicketTagsParams): Promise<IRequestResult<unknown>> =>
    this.patch(`${ticketId}`, getUpdateTagsParams({ ...params }));

  addTags = (ticketId: string, params: IAddTicketTagsParams): Promise<IRequestResult<unknown>> =>
    this.post(`${ticketId}/tags`, { tagIDs: params.tagIds, suggestedTagIDs: params.suggestedTagIds });

  removeTag = (ticketId: string, tagId: string): Promise<IRequestResult<unknown>> =>
    this.delete(`${ticketId}/tags/${tagId}`);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSubject = (ticketId: string, params: IUpdateTicketSubjectParams) => this.patch(`${ticketId}`, { ...params });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updatePriority = (ticketId: string, priority: TicketPriority) => this.patch(`${ticketId}`, { priority });
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateStatus = (ticketId: string, status: TicketStatus) => {
    return this.patch(`${ticketId}`, { status });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updatePendingActions = (
    ticketId: string,
    pendingActions: IPerformBulkActionPayload["pendingActions"],
    visibleTeamIDs: string[],
    tagIDs: string[]
  ) => this.patch(`${ticketId}`, getPendingActionsParams(visibleTeamIDs, tagIDs, { ...pendingActions }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateRequester = (ticketId: string, params: IUpdateTicketRequesterParams) =>
    this.patch(`${ticketId}`, getUpdateRequesterParams({ ...params }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCCs = (ticketId: string, params: IUpdateTicketCCsParams) =>
    this.patch<IUpdateTicketCCsResponse>(`${ticketId}`, getUpdateCCsParams({ ...params }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCustomField = (ticketId: string, params: IUpdateTicketCustomFieldParams) =>
    this.patch<IUpdateTicketCustomFieldResponse>(`${ticketId}`, { ...params });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSilo = (ticketId: string, params: IUpdateTicketSiloParams) => this.put(`${ticketId}/silo`, { ...params });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAssignee = (ticketId: string, params: IUpdateTicketAssigneeParams) =>
    this.patch(`${ticketId}`, getUpdateAssigneeParams({ ...params }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAssignment = (ticketId: string, params: IUpdateTicketAssignmentParams) =>
    this.patch(`${ticketId}`, getUpdateAssignmentParams({ ...params }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendMessage = (ticketId: string, params: ISendTicketMessageParams) =>
    this.patch(`${ticketId}`, getSendMessageParams({ ...params }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  addFollowers = (ticketId: string, followers: string[]) => this.post(`${ticketId}/followers`, { followers });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteFollower = (ticketId: string, agentId: string) => this.delete(`${ticketId}/followers/${agentId}`, {});

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  unspam = (ticketId: string) =>
    this.patch(`${ticketId}`, {
      status: TicketStatus.Open,
      spam: false,
    });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendRating = (ticketId: string, params: IRateTicketParams) =>
    this.post(`${ticketId}/ratings`, getRateTicketParams({ ...params }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteAttachment = (ticketId: string, eventId: number, attachmentId: number) =>
    this.delete(`${ticketId}/events/${eventId}/attachments/${attachmentId}`, {});

  generateSuggestionPrompts = (ticketId: string): Promise<IRequestResult<IReplyPaths[]>> =>
    this.post<IReplyPaths[]>(`${ticketId}/replyPaths`, {});

  generateReplySuggestion = (
    ticketId: string,
    replyPathId: string,
    suggestionId: string
  ): Promise<IRequestResult<IGenerateReplySuggestionResponse>> =>
    this.post<IGenerateReplySuggestionResponse>(
      `${ticketId}/replyPaths/${replyPathId}/replySuggestion/${suggestionId}`,
      {}
    );

  reportPoorPrompts = (ticketId: string, replyPathId: string): Promise<IRequestResult<void>> =>
    this.post(`${ticketId}/replyPaths/${replyPathId}/report`, {});

  rateReplySuggestion = (
    ticketId: string,
    suggestionId: string,
    rating: ReplySuggestionRating
  ): Promise<IRequestResult<void>> => this.post(`${ticketId}/replySuggestions/${suggestionId}/rate`, { rate: rating });
}
